

































import { Vue, Component, Prop } from "vue-property-decorator";
import { getClientId, getMemberClientIds } from "@/data/repository/ClientRepository";
import {
  isPoweredOff,
  getLastUpdateText,
  isIdle,
  isInStandBy,
  isInWarningOrError,
  IresDevice,
  getIresDeviceStatus,
  getIresDeviceDisplayName,
  didIresDeviceCommunicateInLast24Hours,
  isInError,
  isInWarning,
  getIresDeviceCategoryFromSerialNumber,
  IresDeviceCategory,
} from "@/data/models/IresDevice";
import IresDeviceNameComponent from "./IresDeviceName.vue";
import { viewHyDryDeviceDetailsOfMemberAccess } from "@/permissions/UserPermissions";
import { alwaysShowHyDryDeviceStatusClientAccess } from "@/permissions/ClientPermissions";
import { COLOR_HY_DRY_STATUS_ACTIVE, COLOR_HY_DRY_STATUS_ERROR, COLOR_HY_DRY_STATUS_IDLE, COLOR_HY_DRY_STATUS_INACTIVE, COLOR_HY_DRY_STATUS_WARNING } from "@/utils/colors";
import { hasCapability, DeviceCapability } from "@/data/models/DeviceCapability";
import { ComBox } from "@/data/models/ComBox";
import { getEffectiveUser } from "@/utils/ViewAs";

@Component({
  components: {
    "ires-device-name": IresDeviceNameComponent,
  },
})
export default class IresDeviceStatusComponent extends Vue {
  @Prop() iresDevice!: IresDevice;
  @Prop() latestDate: Date | undefined;
  @Prop({ default: true }) showLastUpdateText!: boolean;
  @Prop({ default: true }) showIresDeviceStatus!: boolean;
  @Prop({ default: true }) showDotAfterSerialNumber!: boolean;
  @Prop({ default: true }) showDot!: boolean;
  @Prop({ default: false }) iresDeviceOverviewItem!: boolean;

  @Prop({ default: true }) showInventoryNumber!: boolean;
  @Prop({ default: false }) showInventoryNumberOnSeperateLine!: boolean;
  @Prop({ default: false }) isPanelStatusCard!: boolean;
  @Prop({ default: false }) isClientDetailsSection!: boolean;
  
  @Prop() deviceTemplateName!: string;
  @Prop({ default: true }) inventoryNumberFirst!: boolean;
  @Prop() inventoryNumber!: string;

  private DAY_IN_MILLISECONDS = 24 * 60 * 60 * 1000;

  get assignedClientId() {
    return this.iresDevice?.currentAssignment?.clientId;
  }

  get isMemberClient() {
    return this.assignedClientId ? this.memberClientIds.includes(this.assignedClientId) : false;
  }

  get isAllowedToViewIresDeviceDetailsOfMemberClients() {
    const activeUser = getEffectiveUser();

    return viewHyDryDeviceDetailsOfMemberAccess(activeUser) && this.isMemberClient;
  }

  get memberClientIds() {
    return getMemberClientIds();
  }

  get name() {
    const serialNo = this.iresDevice.serialNo;

    if (!this.showInventoryNumber) {
      return serialNo;
    }

    return getIresDeviceDisplayName(this.iresDevice);
  }

  get hasStatus() {
    return !!getIresDeviceStatus(this.iresDevice);
  }

  get ringColor() {
    if (!this.showDot) {
      return "";
    }

    const showBlackRing =
      !isPoweredOff(this.iresDevice) &&
      didIresDeviceCommunicateInLast24Hours(this.iresDevice, this.latestDate);

    return `0 0 0 1px ${showBlackRing ? "black" : this.statusColor}`;
  }

  get style() {
    return this.iresDeviceOverviewItem
      ? "min-width: 0; overflow: hidden"
      : "";
  }

  get statusColor() {
    if (!this.showDot) {
      return "";
    }

    if (this.hasPowerFail) {
      return COLOR_HY_DRY_STATUS_INACTIVE;
    }

    if (this.hasError) {
      return COLOR_HY_DRY_STATUS_ERROR;
    }

    if (this.hasWarning) {
      return COLOR_HY_DRY_STATUS_WARNING;
    }

    if (this.isInStandBy) {
      return COLOR_HY_DRY_STATUS_INACTIVE;
    }

    if (this.isIdle) {
      return COLOR_HY_DRY_STATUS_IDLE;
    }

    return COLOR_HY_DRY_STATUS_ACTIVE;
  }

  get showLatestInfo() {    
    return (
      this.showIresDeviceStatus &&
      (
        alwaysShowHyDryDeviceStatusClientAccess() ||
        getClientId() === this.assignedClientId ||
        this.isAllowedToViewIresDeviceDetailsOfMemberClients
      )
    );
  }

  get panelNameCols() {
    return this.showLastUpdateText ? 5 : 11;
  }

  // ==================== FIRMWARE v4.0.0+ CHANGES ====================
  get doesSupportExtendedSensors() {
    if (!this.iresDevice || getIresDeviceCategoryFromSerialNumber(this.iresDevice.serialNo) === IresDeviceCategory.PANEL) {
      return false;
    }
    
    return hasCapability(this.iresDevice as ComBox, DeviceCapability.EXTENDED_SENSORS);
  }

  get hasWarningOrError(): boolean {
    return isInWarningOrError(this.iresDevice, this.doesSupportExtendedSensors);
  }

  get hasError(): boolean {
    return isInError(this.iresDevice, this.doesSupportExtendedSensors);
  }

  get hasWarning(): boolean {
    return isInWarning(this.iresDevice, this.doesSupportExtendedSensors);
  }

  get isIdle() {
    return isIdle(this.iresDevice);
  }

  get isInStandBy() {
    return isInStandBy(this.iresDevice);
  }

  get hasPowerFail(): boolean {
    return isPoweredOff(this.iresDevice);
  }

  get lastUpdateText(): string {
    const iresDeviceStatus = getIresDeviceStatus(this.iresDevice);
    const ref = this.latestDate;

    if (!iresDeviceStatus || !ref || !iresDeviceStatus.createdAt) {
      return "";
    }

    return getLastUpdateText(this, ref, iresDeviceStatus, true);
  }
}
